/**
 * Frontend-only integration capability fixture, used to render the
 * per-action sub-forms inside the OUTPUT_INTEGRATION inspector.
 *
 * Mirrors the shape of the forthcoming `integrationCapabilities(type)`
 * GraphQL query from the primitives implementation plan §3.3.2.
 */

import type { IntegrationType } from '~/pages/workflows/builder/types';

export const INTEGRATION_TYPES: IntegrationType[] = [
  {
    key: 'ams360',
    label: 'Vertafore AMS360',
    capabilities: [
      {
        key: 'create_policy',
        label: 'Create policy',
        description: 'Adds a new policy record to AMS360',
        configFields: [
          { key: 'lineOfBusiness', label: 'Line of business', type: 'string', required: true },
          {
            key: 'accountId',
            label: 'Account ID',
            type: 'string',
            required: true,
            description: 'Numeric AMS360 account id'
          }
        ]
      },
      {
        key: 'upload_document',
        label: 'Upload document',
        description: 'Attaches a document to an account',
        configFields: [
          { key: 'accountId', label: 'Account ID', type: 'string', required: true },
          {
            key: 'category',
            label: 'Category',
            type: 'enum',
            required: true,
            options: [
              { value: 'application', label: 'Application' },
              { value: 'policy', label: 'Policy' },
              { value: 'endorsement', label: 'Endorsement' },
              { value: 'correspondence', label: 'Correspondence' }
            ]
          }
        ]
      },
      {
        key: 'create_activity',
        label: 'Create activity',
        description: 'Logs an activity note',
        configFields: [
          { key: 'accountId', label: 'Account ID', type: 'string', required: true },
          { key: 'subject', label: 'Subject', type: 'string', required: true },
          { key: 'body', label: 'Body', type: 'string', required: false, multiline: true }
        ]
      }
    ]
  },
  {
    key: 'applied_epic',
    label: 'Applied Epic',
    capabilities: [
      {
        key: 'create_policy',
        label: 'Create policy',
        description: 'Adds a new policy record to Applied Epic',
        configFields: [
          { key: 'clientId', label: 'Client ID', type: 'string', required: true },
          { key: 'lineOfBusiness', label: 'Line of business', type: 'string', required: true }
        ]
      },
      {
        key: 'upload_document',
        label: 'Upload document',
        description: 'Attaches a document to a client',
        configFields: [
          { key: 'clientId', label: 'Client ID', type: 'string', required: true },
          { key: 'attachmentType', label: 'Attachment type', type: 'string', required: false }
        ]
      }
    ]
  },
  {
    key: 'hawksoft',
    label: 'Hawksoft',
    capabilities: [
      {
        key: 'attach_file',
        label: 'Attach file',
        description: 'Attaches a rendered file to the client record',
        configFields: [{ key: 'clientId', label: 'Client ID', type: 'string', required: true }]
      },
      {
        key: 'create_suspense',
        label: 'Create suspense',
        description: 'Schedules a follow-up',
        configFields: [
          { key: 'clientId', label: 'Client ID', type: 'string', required: true },
          { key: 'subject', label: 'Subject', type: 'string', required: true },
          { key: 'dueInDays', label: 'Due in (days)', type: 'number', required: false, default: 7 }
        ]
      }
    ]
  },
  {
    key: 'ezlynx',
    label: 'EZLynx',
    capabilities: [
      {
        key: 'create_policy',
        label: 'Create policy',
        description: 'Adds a new policy record',
        configFields: [
          { key: 'applicantId', label: 'Applicant ID', type: 'string', required: true }
        ]
      }
    ]
  },
  {
    key: 'nowcerts',
    label: 'NowCerts',
    capabilities: [
      {
        key: 'create_policy',
        label: 'Create policy',
        description: 'Adds a new policy record',
        configFields: [{ key: 'insuredId', label: 'Insured ID', type: 'string', required: true }]
      },
      {
        key: 'request_signature',
        label: 'Request e-signature',
        description: 'Sends a signature request for a document',
        configFields: [
          { key: 'insuredId', label: 'Insured ID', type: 'string', required: true },
          { key: 'signerEmail', label: 'Signer email', type: 'string', required: true }
        ]
      }
    ]
  },
  {
    key: 'agencyzoom',
    label: 'AgencyZoom',
    capabilities: [
      {
        key: 'create_lead',
        label: 'Create lead',
        description: 'Adds a new lead',
        configFields: [{ key: 'pipeline', label: 'Pipeline', type: 'string', required: true }]
      }
    ]
  }
];

export const INTEGRATION_BY_KEY: Record<string, IntegrationType | undefined> = Object.fromEntries(
  INTEGRATION_TYPES.map((i) => [i.key, i])
);
